import { useAuth } from "../contexts/useAuth";
import { PageLoadError } from "../components/PageLoadError";
import { Dashboard } from "../Dashboard";
import { AdminDashboardPage } from "./AdminDashboardPage";
import { GetDashboardDataFn } from "../hooks/useDashboard";
import { useQueries } from "@tanstack/react-query";
import { GetOrganisationDetailsFn } from "../hooks/useOrganisation.ts";
import { normalizeLabType } from "../types/lab";

export const ConnectedDashboardPage = () => {
  const { user } = useAuth();
  const organisationId = user?.organisation_id;
  const isAdmin = user?.is_organisation_admin ?? false;

  // const isAuditor = false;

  const [dashboard, organisation] = useQueries({
    queries: [
      {
        queryKey: ["dashboard"],
        queryFn: GetDashboardDataFn,
        enabled: !!user,
        retryOnMount: true,
        refetchOnMount: "always",
        refetchOnReconnect: true,
      },
      {
        queryKey: ["organisation", organisationId],
        queryFn: async () => {
          if (!organisationId) {
            throw new Error("Organisation ID is required");
          }
          return await GetOrganisationDetailsFn(organisationId);
        },
        enabled: !!organisationId,
        retryOnMount: true,
        refetchOnMount: "always",
        refetchOnReconnect: true,
      },
    ],
  });

  const retryPageQueries = async () => {
    await Promise.all([dashboard.refetch(), organisation.refetch()]);
  };



  if (!user || !organisationId) {
    return null;
  }

  if (dashboard.isLoading || organisation.isLoading) {
    return <div className="skeleton h-4 w-full"></div>;
  }

  if (dashboard.isError) {
    return (
      <PageLoadError
        message="Error loading dashboard data"
        onRetry={retryPageQueries}
        isRetrying={dashboard.isFetching || organisation.isFetching}
      />
    );
  }

  if (organisation.isError) {
    return (
      <PageLoadError
        message="Error loading organisation data"
        onRetry={retryPageQueries}
        isRetrying={dashboard.isFetching || organisation.isFetching}
      />
    );
  }

  if (!organisation.data) {
    return (
      <div className="alert alert-error">Organisation data not available</div>
    );
  }

  const dashboardData = dashboard.data;

  if (!dashboardData) {
    return (
      <PageLoadError
        message="Error loading dashboard data"
        onRetry={retryPageQueries}
        isRetrying={dashboard.isFetching || organisation.isFetching}
      />
    );
  }

  const allSubscriptionTypes = Array.from(
    (dashboardData.all_labs ?? []).reduce<Set<string>>((acc, current) => {
      const labType = normalizeLabType(current.type);
      acc.add(labType);
      return acc;
    }, new Set<string>()),
  );

  // if (isAuditor) {
  //   return (
  //     <AuditorDashboardPage
  //       organisationName={organisation.data.name}
  //       labTypeSubscriptions={allSubscriptionTypes}
  //     />
  //   );
  // }

  if (isAdmin) {
    return (
      <AdminDashboardPage
        assignedAudits={dashboardData.assigned_audits}
        organisationName={organisation.data.name}
        labTypeSubscriptions={allSubscriptionTypes}
        organisation={organisation.data}
      />
    );
  }

  return <Dashboard organisation={organisation.data} />;
};
